import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { TokenService } from '../services/token.service';
import { NativeService } from '../services/native.service';
import { AuthenticationService } from '@shared/services/authentication.service';
import { SessionIdService } from '@shared/services/sessionId.service';
import { DeviceService } from '@shared/services/device.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '@shared/services/logger.service';
import { AppPermissionsRepository } from '../state/permissions/app-permissions.repository';

@Injectable({
  providedIn: 'root',
})
export class TokenInterceptor implements HttpInterceptor {
  private translateService = inject(TranslateService);
  private tokenService = inject(TokenService);
  private nativeService = inject(NativeService);
  private authenticationService = inject(AuthenticationService);
  private deviceService = inject(DeviceService);
  private sessionIdService = inject(SessionIdService);
  private appPermissionsRepo = inject(AppPermissionsRepository);
  private logger = inject(LoggerService);

  private permissionsQuery: string;

  constructor() {
    this.appPermissionsRepo.appPermissions$.subscribe((value) => {
      const dictionary = Object.fromEntries(Object.keys(value).map((key) => [key, value[key]]));
      this.permissionsQuery = new URLSearchParams(dictionary).toString();
    });
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isAuthorizedUrl(request.url)) {
      return next.handle(request);
    }

    return next.handle(this.addCustomHeaders(request)).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          return this.handleHttpErrors(error, request, next);
        }
        return throwError(() => error);
      })
    );
  }

  private isAuthorizedUrl(url: string): boolean {
    return url.startsWith(environment.apiUrl) || url.startsWith(environment.shopOffersUrl);
  }

  private handleHttpErrors(
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (error.status === 401) {
      // These endpoints must never trigger a token refresh
      const noRefreshUrls = ['login/email', 'logout', 'login/refreshtoken'];
      if (noRefreshUrls.some((url) => request.url.includes(url))) {
        return throwError(() => error);
      }

      return this.refreshTokenOn401Error(request, next);
    }

    return throwError(() => error);
  }

  private refreshTokenOn401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.tokenService.getValidToken().pipe(
      switchMap(() => next.handle(this.addCustomHeaders(request))),
      catchError((err) => {
        this.authenticationService.logout().catch((e) => this.logger.error(e));
        return throwError(() => err);
      })
    );
  }

  private addCustomHeaders(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const headers: Record<string, string> = {
      'Accept-Language': this.translateService.currentLang ?? this.translateService.defaultLang ?? 'es',
      'sg-device-type': `${this.deviceService.deviceType}`,
      'sg-environment': `${this.deviceService.deviceEnvironment}`,
      'sg-game': 'slotcom',
      'sg-version-app': `${environment.VERSION}`, // Not using this.versionRepo.getFrontendVersion() to avoid initialization problems
      'sg-version-native': `${this.nativeService.nativeInfo.version}`,
      'sg-device-os': `${this.nativeService.nativeInfo.operatingSystem}`,
      'sg-device-model': `${this.nativeService.nativeInfo.hardwareModel}`,
      AppVersion: `slotcom-${this.nativeService.nativeInfo.deviceType}-${environment.VERSION}`, // this should be eliminated
      'sc-app-permissions': `${this.permissionsQuery}`,
    };

    if (this.sessionIdService.sessionId) {
      headers['sg-session'] = this.sessionIdService.sessionId;
      headers['sg-device-id'] = this.deviceService.deviceId;
    }

    const token = this.tokenService.getCurrentToken();
    if (token?.token) {
      headers['Authorization'] = `Bearer ${token.token}`;
    }

    return request.clone({ setHeaders: headers });
  }
}
